import { createContext, useContext, useState, useEffect, type ReactNode } from "react";

export interface TypographyScale {
  // Hero title
  heroSize: string;        // clamp value for the main event name
  heroWeight: string;      // font-weight
  heroTracking: string;    // letter-spacing
  heroLineHeight: string;  // line-height
  heroTransform: string;   // text-transform (uppercase, none, capitalize)
  heroStyle: string;       // font-style (normal, italic)
  // Section headings (e.g., "The Collection", "A Curated Afternoon")
  sectionSize: string;
  sectionWeight: string;
  sectionTracking: string;
  sectionStyle: string;
  // Labels (e.g., "Access & Reservations", meta labels)
  labelSize: string;
  labelWeight: string;
  labelTracking: string;
  // Body text
  bodySize: string;
  bodyWeight: string;
  bodyLineHeight: string;
  // CTA buttons
  ctaSize: string;
  ctaWeight: string;
  ctaTracking: string;
  ctaPadding: string;
  ctaRadius: string;
  // Countdown
  countdownSize: string;
  countdownWeight: string;
}

export interface ColorTheme {
  key: string;
  name: string;
  desc: string;
  ink: string;
  cream: string;
  gold: string;
  fontDisplay: string;
  fontSans: string;
  fontSerif: string;
  fontHeading: string;
  fontUrl: string;
  typo: TypographyScale;
}

const originalTypo: TypographyScale = {
  heroSize: "clamp(2.25rem, 8vw, 7rem)",
  heroWeight: "400",
  heroTracking: "0.05em",
  heroLineHeight: "1.1",
  heroTransform: "uppercase",
  heroStyle: "normal",
  sectionSize: "clamp(2rem, 4vw, 4rem)",
  sectionWeight: "800",
  sectionTracking: "0.15em",
  sectionStyle: "normal",
  labelSize: "0.85rem",
  labelWeight: "800",
  labelTracking: "0.2em",
  bodySize: "1.35rem",
  bodyWeight: "400",
  bodyLineHeight: "1.65",
  ctaSize: "0.85rem",
  ctaWeight: "600",
  ctaTracking: "0.1em",
  ctaPadding: "1.25rem 2.5rem",
  ctaRadius: "0",
  countdownSize: "clamp(2rem, 5vw, 3.5rem)",
  countdownWeight: "800",
};

export const themes: ColorTheme[] = [
  {
    key: "original",
    name: "Original",
    desc: "Warm ink, cream & gold",
    ink: "#2C2520",
    cream: "#F0EAE3",
    gold: "#D8B05E",
    fontDisplay: "'Marcellus', Georgia, serif",
    fontSans: "'Jost', sans-serif",
    fontSerif: "'Cormorant Garamond', Georgia, serif",
    fontHeading: "'Montserrat', sans-serif",
    fontUrl: "",
    typo: originalTypo,
  },
  {
    key: "midnight",
    name: "Midnight Frost",
    desc: "Deep navy · Bodoni Moda · High fashion editorial",
    ink: "#0D1B2A",
    cream: "#E8ECF1",
    gold: "#A8B4C4",
    fontDisplay: "'Bodoni Moda', Georgia, serif",
    fontSans: "'Inter', sans-serif",
    fontSerif: "'Playfair Display', Georgia, serif",
    fontHeading: "'Urbanist', sans-serif",
    fontUrl: "https://fonts.googleapis.com/css2?family=Bodoni+Moda:ital,wght@0,400;0,700;1,400&family=Inter:wght@300;400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Urbanist:wght@300;400;600;800&display=swap",
    typo: {
      heroSize: "clamp(3rem, 10vw, 9rem)",
      heroWeight: "400",
      heroTracking: "0.02em",
      heroLineHeight: "0.95",
      heroTransform: "uppercase",
      heroStyle: "italic",
      sectionSize: "clamp(2.5rem, 5vw, 5rem)",
      sectionWeight: "400",
      sectionTracking: "0.02em",
      sectionStyle: "italic",
      labelSize: "0.8rem",
      labelWeight: "600",
      labelTracking: "0.25em",
      bodySize: "1.3rem",
      bodyWeight: "300",
      bodyLineHeight: "1.7",
      ctaSize: "0.8rem",
      ctaWeight: "500",
      ctaTracking: "0.2em",
      ctaPadding: "1.1rem 2.8rem",
      ctaRadius: "0",
      countdownSize: "clamp(2.5rem, 6vw, 4.5rem)",
      countdownWeight: "400",
    },
  },
  {
    key: "noir",
    name: "Gilded Noir",
    desc: "True black · Italiana · Art Deco speakeasy",
    ink: "#0A0A0A",
    cream: "#F5F0EB",
    gold: "#C9A77D",
    fontDisplay: "'Italiana', Georgia, serif",
    fontSans: "'Outfit', sans-serif",
    fontSerif: "'Cormorant Infant', Georgia, serif",
    fontHeading: "'Josefin Sans', sans-serif",
    fontUrl: "https://fonts.googleapis.com/css2?family=Italiana&family=Outfit:wght@300;400;500;600;700&family=Cormorant+Infant:ital,wght@0,300;0,400;0,600;1,400&family=Josefin+Sans:wght@300;400;600;700&display=swap",
    typo: {
      heroSize: "clamp(2.5rem, 9vw, 8rem)",
      heroWeight: "400",
      heroTracking: "0.12em",
      heroLineHeight: "1.05",
      heroTransform: "uppercase",
      heroStyle: "normal",
      sectionSize: "clamp(2rem, 4.5vw, 4.5rem)",
      sectionWeight: "300",
      sectionTracking: "0.2em",
      sectionStyle: "normal",
      labelSize: "0.85rem",
      labelWeight: "300",
      labelTracking: "0.3em",
      bodySize: "1.35rem",
      bodyWeight: "300",
      bodyLineHeight: "1.65",
      ctaSize: "0.8rem",
      ctaWeight: "400",
      ctaTracking: "0.25em",
      ctaPadding: "1.2rem 3rem",
      ctaRadius: "0",
      countdownSize: "clamp(2.5rem, 6vw, 5rem)",
      countdownWeight: "400",
    },
  },
  {
    key: "emerald",
    name: "Emerald Reserve",
    desc: "Deep forest · DM Serif · British editorial",
    ink: "#1A2E2A",
    cream: "#EDE9E3",
    gold: "#2E8B6E",
    fontDisplay: "'DM Serif Display', Georgia, serif",
    fontSans: "'DM Sans', sans-serif",
    fontSerif: "'Lora', Georgia, serif",
    fontHeading: "'Spline Sans', sans-serif",
    fontUrl: "https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600;700&family=Lora:ital,wght@0,400;0,600;1,400&family=Spline+Sans:wght@300;400;600;700&display=swap",
    typo: {
      heroSize: "clamp(2.5rem, 8vw, 7rem)",
      heroWeight: "400",
      heroTracking: "0.01em",
      heroLineHeight: "1.1",
      heroTransform: "none",
      heroStyle: "normal",
      sectionSize: "clamp(2rem, 4vw, 3.5rem)",
      sectionWeight: "400",
      sectionTracking: "0",
      sectionStyle: "italic",
      labelSize: "0.85rem",
      labelWeight: "600",
      labelTracking: "0.15em",
      bodySize: "1.35rem",
      bodyWeight: "400",
      bodyLineHeight: "1.65",
      ctaSize: "0.85rem",
      ctaWeight: "500",
      ctaTracking: "0.1em",
      ctaPadding: "1.1rem 2.2rem",
      ctaRadius: "4px",
      countdownSize: "clamp(2rem, 5vw, 3.5rem)",
      countdownWeight: "400",
    },
  },
  {
    key: "dusk",
    name: "Velvet Dusk",
    desc: "Smoky plum · EB Garamond · Twilight romance",
    ink: "#2D1A2E",
    cream: "#F2ECF0",
    gold: "#C7A4B8",
    fontDisplay: "'Tenor Sans', sans-serif",
    fontSans: "'Nunito Sans', sans-serif",
    fontSerif: "'EB Garamond', Georgia, serif",
    fontHeading: "'Rubik', sans-serif",
    fontUrl: "https://fonts.googleapis.com/css2?family=Tenor+Sans&family=Nunito+Sans:wght@300;400;600;700&family=EB+Garamond:ital,wght@0,400;0,600;1,400&family=Rubik:wght@300;400;600;800&display=swap",
    typo: {
      heroSize: "clamp(2.25rem, 7vw, 6rem)",
      heroWeight: "400",
      heroTracking: "0.08em",
      heroLineHeight: "1.15",
      heroTransform: "uppercase",
      heroStyle: "normal",
      sectionSize: "clamp(2rem, 4vw, 3.5rem)",
      sectionWeight: "400",
      sectionTracking: "0.05em",
      sectionStyle: "italic",
      labelSize: "0.85rem",
      labelWeight: "600",
      labelTracking: "0.18em",
      bodySize: "1.35rem",
      bodyWeight: "400",
      bodyLineHeight: "1.65",
      ctaSize: "0.85rem",
      ctaWeight: "600",
      ctaTracking: "0.12em",
      ctaPadding: "1.15rem 2.5rem",
      ctaRadius: "999px",
      countdownSize: "clamp(2rem, 5vw, 3.5rem)",
      countdownWeight: "400",
    },
  },
  {
    key: "voltage",
    name: "Carbon Voltage",
    desc: "Carbon black · Syne · Electric, futuristic",
    ink: "#141419",
    cream: "#EAECF0",
    gold: "#3D7BF7",
    fontDisplay: "'Syne', sans-serif",
    fontSans: "'Space Grotesk', sans-serif",
    fontSerif: "'Fraunces', Georgia, serif",
    fontHeading: "'Chakra Petch', sans-serif",
    fontUrl: "https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Space+Grotesk:wght@300;400;500;600;700&family=Fraunces:ital,wght@0,400;0,600;1,400&family=Chakra+Petch:wght@300;400;600;700&display=swap",
    typo: {
      heroSize: "clamp(2.75rem, 9vw, 8rem)",
      heroWeight: "800",
      heroTracking: "-0.02em",
      heroLineHeight: "1.0",
      heroTransform: "uppercase",
      heroStyle: "normal",
      sectionSize: "clamp(2rem, 4.5vw, 4rem)",
      sectionWeight: "700",
      sectionTracking: "-0.01em",
      sectionStyle: "normal",
      labelSize: "0.85rem",
      labelWeight: "600",
      labelTracking: "0.2em",
      bodySize: "1.3rem",
      bodyWeight: "400",
      bodyLineHeight: "1.65",
      ctaSize: "0.85rem",
      ctaWeight: "700",
      ctaTracking: "0.08em",
      ctaPadding: "1.1rem 2.5rem",
      ctaRadius: "2px",
      countdownSize: "clamp(2.5rem, 6vw, 4.5rem)",
      countdownWeight: "800",
    },
  },
];

interface ThemeContextValue {
  theme: ColorTheme;
  setTheme: (key: string) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: themes[0],
  setTheme: () => {},
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themeKey, setThemeKey] = useState("midnight");

  const theme = themes.find((t) => t.key === themeKey) || themes[0];


  // Inject Google Fonts link — but skip the default "midnight" theme since
  // those fonts are already loaded directly from index.html (non-blocking).
  useEffect(() => {
    const existing = document.getElementById("theme-fonts");
    if (existing) existing.remove();

    if (theme.fontUrl && theme.key !== "midnight") {
      const link = document.createElement("link");
      link.id = "theme-fonts";
      link.rel = "stylesheet";
      link.href = theme.fontUrl;
      document.head.appendChild(link);
    }
  }, [theme.fontUrl, theme.key]);

  // Apply CSS custom properties to :root
  useEffect(() => {
    const root = document.documentElement;
    const t = theme.typo;

    // Colors as HSL for Tailwind opacity support
    const inkHsl = hexToHsl(theme.ink);
    const creamHsl = hexToHsl(theme.cream);
    const goldHsl = hexToHsl(theme.gold);

    root.style.setProperty("--ink-hsl", inkHsl);
    root.style.setProperty("--cream-hsl", creamHsl);
    root.style.setProperty("--gold-hsl", goldHsl);
    root.style.setProperty("--foreground", inkHsl);

    // Update semantic design tokens to match theme
    root.style.setProperty("--background", creamHsl);
    root.style.setProperty("--card", lightenHsl(creamHsl, 4));
    root.style.setProperty("--card-foreground", inkHsl);
    root.style.setProperty("--popover", lightenHsl(creamHsl, 4));
    root.style.setProperty("--popover-foreground", inkHsl);
    root.style.setProperty("--primary", goldHsl);
    root.style.setProperty("--primary-foreground", inkHsl);
    root.style.setProperty("--secondary", creamHsl);
    root.style.setProperty("--secondary-foreground", inkHsl);
    root.style.setProperty("--muted", adjustHsl(creamHsl, -5));
    root.style.setProperty("--muted-foreground", adjustHsl(inkHsl, 30));
    root.style.setProperty("--accent", goldHsl);
    root.style.setProperty("--accent-foreground", inkHsl);
    root.style.setProperty("--border", inkHsl + " / 0.15");
    root.style.setProperty("--input", inkHsl + " / 0.15");
    root.style.setProperty("--ring", goldHsl);

    // Fonts
    root.style.setProperty("--font-display", theme.fontDisplay);
    root.style.setProperty("--font-sans", theme.fontSans);
    root.style.setProperty("--font-serif-elegant", theme.fontSerif);
    root.style.setProperty("--font-heading", theme.fontHeading);

    // Typography scale
    root.style.setProperty("--hero-size", t.heroSize);
    root.style.setProperty("--hero-weight", t.heroWeight);
    root.style.setProperty("--hero-tracking", t.heroTracking);
    root.style.setProperty("--hero-line-height", t.heroLineHeight);
    root.style.setProperty("--hero-transform", t.heroTransform);
    root.style.setProperty("--hero-style", t.heroStyle);
    root.style.setProperty("--section-size", t.sectionSize);
    root.style.setProperty("--section-weight", t.sectionWeight);
    root.style.setProperty("--section-tracking", t.sectionTracking);
    root.style.setProperty("--section-style", t.sectionStyle);
    root.style.setProperty("--label-size", t.labelSize);
    root.style.setProperty("--label-weight", t.labelWeight);
    root.style.setProperty("--label-tracking", t.labelTracking);
    root.style.setProperty("--body-size", t.bodySize);
    root.style.setProperty("--body-weight", t.bodyWeight);
    root.style.setProperty("--body-line-height", t.bodyLineHeight);
    root.style.setProperty("--cta-size", t.ctaSize);
    root.style.setProperty("--cta-weight", t.ctaWeight);
    root.style.setProperty("--cta-tracking", t.ctaTracking);
    root.style.setProperty("--cta-padding", t.ctaPadding);
    root.style.setProperty("--cta-radius", t.ctaRadius);
    root.style.setProperty("--countdown-size", t.countdownSize);
    root.style.setProperty("--countdown-weight", t.countdownWeight);

    document.body.style.fontFamily = theme.fontSans;

    return () => {
      const props = [
        "--ink-hsl", "--cream-hsl", "--gold-hsl", "--foreground",
        "--background", "--card", "--card-foreground", "--popover", "--popover-foreground",
        "--primary", "--primary-foreground", "--secondary", "--secondary-foreground",
        "--muted", "--muted-foreground", "--accent", "--accent-foreground",
        "--border", "--input", "--ring",
        "--font-display", "--font-sans", "--font-serif-elegant", "--font-heading",
        "--hero-size", "--hero-weight", "--hero-tracking", "--hero-line-height", "--hero-transform", "--hero-style",
        "--section-size", "--section-weight", "--section-tracking", "--section-style",
        "--label-size", "--label-weight", "--label-tracking",
        "--body-size", "--body-weight", "--body-line-height",
        "--cta-size", "--cta-weight", "--cta-tracking", "--cta-padding", "--cta-radius",
        "--countdown-size", "--countdown-weight",
      ];
      props.forEach((p) => root.style.removeProperty(p));
      document.body.style.fontFamily = "";
    };
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme: setThemeKey }}>
      {children}
    </ThemeContext.Provider>
  );
}

function lightenHsl(hsl: string, amount: number): string {
  const parts = hsl.match(/(\d+)\s+(\d+)%\s+(\d+)%/);
  if (!parts) return hsl;
  const l = Math.min(100, parseInt(parts[3]) + amount);
  return `${parts[1]} ${parts[2]}% ${l}%`;
}

function adjustHsl(hsl: string, lightnessShift: number): string {
  const parts = hsl.match(/(\d+)\s+(\d+)%\s+(\d+)%/);
  if (!parts) return hsl;
  const l = Math.max(0, Math.min(100, parseInt(parts[3]) + lightnessShift));
  return `${parts[1]} ${parts[2]}% ${l}%`;
}

export function useTheme() {
  return useContext(ThemeContext);
}

function hexToHsl(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
